import { createEffect, createSignal, onCleanup } from 'solid-js'
import { NumericalDataType } from '../models/numerical-data-type'

export type DecimalFormatterFunc = (value?: number, numFractionDigits?: number) => string | undefined
export type NumericalFormatterFunc = (type: NumericalDataType, value?: number, numFractionDigits?: number) => string | undefined

export interface currencyFormatterResult {
  readonly currencyFormatter: DecimalFormatterFunc
  readonly numberFormatter: (value: number, numFractionDigits?: number) => string | undefined
  readonly percentageFormatter: DecimalFormatterFunc
  readonly formatNumericValue: NumericalFormatterFunc
}

const PERCENTAGE_MULTIPLIER = 100
const DEFAULT_LOCALE = 'en-US'

export const resolveLocale = (language: string | undefined): string => {
  if (!language) {
    return DEFAULT_LOCALE
  }

  try {
    return Intl.getCanonicalLocales(language)[0] ?? DEFAULT_LOCALE
  } catch {
    return DEFAULT_LOCALE
  }
}

export const useNumberFormatter = (): currencyFormatterResult => {
  const [locale, setLocale] = createSignal<string>()

  const currency = (): string => 'USD'

  const handleLanguageChange = (): void => {
    setLocale(resolveLocale(window.navigator.language))
  }

  window.addEventListener('languagechange', handleLanguageChange)
  onCleanup(() => {
    window.removeEventListener('languagechange', handleLanguageChange)
  })

  createEffect(() => {
    setLocale(resolveLocale(window.navigator.language))
  })

  const currencyFormatter = (value?: number, numFractionDigits: number = 0): string => {
    return new Intl
      .NumberFormat(locale(), { style: 'currency', currency: currency(), maximumFractionDigits: numFractionDigits, minimumFractionDigits: numFractionDigits })
      .format(value!)
  }

  const numberFormatter = (value?: number, numFractionDigits: number = 0): string | undefined => {
    if (!value) {
      return undefined
    }

    return Intl.NumberFormat(locale(), { maximumFractionDigits: 2, minimumFractionDigits: numFractionDigits }).format(value)
  }

  const percentageFormatter = (value: number | undefined, numFractionDigits: number = 0): string | undefined => {
    if (!value) {
      return undefined
    }

    return `${numberFormatter(value * PERCENTAGE_MULTIPLIER, numFractionDigits)}%`
  }

  const formatNumericValue = (type: NumericalDataType, value: number | undefined, numFractionDigits?: number): string | undefined => {
    switch (type) {
      case NumericalDataType.Currency:
        return currencyFormatter(value, numFractionDigits)
      case NumericalDataType.Percentage:
        return percentageFormatter(value, numFractionDigits)
      default:
        return numberFormatter(value, numFractionDigits)
    }
  }

  return {
    currencyFormatter,
    numberFormatter,
    percentageFormatter,
    formatNumericValue
  }
}
